// ============================================
// Schemas de validación para Mantenimiento de Plotters
// ============================================

import { z } from 'zod';

// Schema para consumibles usados
export const consumableSchema = z.object({
  name: z.string().min(1, 'Nombre requerido'),
  quantity: z.number().min(0.01, 'Cantidad debe ser mayor a 0'),
  cost: z.number().min(0).optional(),
});

// Schema para items del checklist
export const checklistItemSchema = z.object({
  task: z.string().min(1, 'Tarea requerida'),
  completed: z.boolean().default(false),
});

// Schema principal para crear/editar tareas de mantenimiento
export const plotterMaintenanceFormSchema = z.object({
  machine_id: z.string().uuid('Selecciona un plotter'),
  task_type: z.enum([
    'routine_cleaning',
    'blade_change',
    'calibration',
    'lubrication',
    'deep_cleaning',
    'inspection',
    'other'
  ], {
    required_error: 'Selecciona un tipo de tarea',
  }),
  title: z.string()
    .min(3, 'Mínimo 3 caracteres')
    .max(255, 'Máximo 255 caracteres'),
  description: z.string()
    .max(2000, 'Máximo 2000 caracteres')
    .optional()
    .or(z.literal('')),
  scheduled_date: z.string().min(1, 'Fecha requerida'),
  estimated_duration_minutes: z.number()
    .int('Debe ser número entero')
    .min(1, 'Mínimo 1 minuto')
    .max(480, 'Máximo 8 horas'),
  is_recurring: z.boolean().default(false),
  recurrence_pattern: z.enum([
    'daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'
  ]).optional().nullable(),
  recurrence_interval: z.number().int().min(1).max(365).default(1),
  assigned_to: z.string().uuid().optional().nullable().or(z.literal('')),
  priority: z.number().int().min(1).max(10).default(5),
  notes: z.string().max(2000).optional().or(z.literal('')),
  checklist: z.array(checklistItemSchema).default([]),
}).refine(
  (data) => !data.is_recurring || data.recurrence_pattern,
  {
    message: 'Patrón de recurrencia requerido para tareas recurrentes',
    path: ['recurrence_pattern'],
  }
);

// Schema para completar una tarea de mantenimiento
export const completeMaintenanceSchema = z.object({
  actual_duration_minutes: z.number()
    .int('Debe ser número entero')
    .min(1, 'Duración requerida'),
  operating_hours_at_service: z.number().int().min(0).optional().nullable(),
  parts_replaced: z.array(z.string()).optional().default([]),
  consumables_used: z.array(consumableSchema).default([]),
  labor_cost: z.number().min(0).optional().nullable(),
  parts_cost: z.number().min(0).optional().nullable(),
  notes: z.string().max(2000).optional().or(z.literal('')),
  checklist: z.array(checklistItemSchema),
});

// Schema para filtros de búsqueda
export const maintenanceFiltersSchema = z.object({
  machine_id: z.string().uuid().optional().nullable(),
  status: z.enum(['scheduled', 'in_progress', 'completed', 'skipped', 'overdue', 'all']).optional(),
  task_type: z.enum([
    'routine_cleaning', 'blade_change', 'calibration',
    'lubrication', 'deep_cleaning', 'inspection', 'other', 'all'
  ]).optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});

// Tipos inferidos de los schemas
export type PlotterMaintenanceFormData = z.infer<typeof plotterMaintenanceFormSchema>;
export type CompleteMaintenanceFormData = z.infer<typeof completeMaintenanceSchema>;
export type MaintenanceFilters = z.infer<typeof maintenanceFiltersSchema>;
